import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import ResponsivenessBadge from "../../components/ResponsivenessBadge";
import { isBrandPro } from "../../lib/plan";
import { toast } from "sonner";
import { Trophy, MapPin, Sparkles, BookmarkCheck, Send, ArrowLeft, Quote, Mail, Lock, CheckCircle2, Clock, Phone } from "lucide-react";

export default function AthleteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [shortlisted, setShortlisted] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactStatus, setContactStatus] = useState({ status: "none" }); // {status, contact?, request?}
  const [loading, setLoading] = useState(true);

  const loadContact = () => {
    api.get(`/brand/contact-request/${id}`)
      .then(({ data }) => setContactStatus(data))
      .catch(() => {});
  };

  useEffect(() => {
    api.get(`/directory/athletes/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Athlete not found in directory"))
      .finally(() => setLoading(false));
    api.get("/brand/shortlist").then(({ data }) => {
      if (data.find(x => x.athlete_user_id === id)) setShortlisted(true);
    }).catch(() => {});
    loadContact();
    // eslint-disable-next-line
  }, [id]);

  const addShortlist = async () => {
    try { await api.post("/brand/shortlist", { athlete_user_id: id }); setShortlisted(true); toast.success("Added to shortlist"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeShortlist = async () => {
    try { await api.delete(`/brand/shortlist/${id}`); setShortlisted(false); toast.success("Removed from shortlist"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <BrandDashboardLayout><div className="p-12 text-ink-muted">Loading…</div></BrandDashboardLayout>;
  if (!data) return <BrandDashboardLayout><div className="p-12 text-ink-muted">Not found</div></BrandDashboardLayout>;

  const p = data.profile || {};
  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12 max-w-5xl">
        <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-widest text-ink-muted hover:text-white inline-flex items-center gap-2 mb-8" data-testid="athlete-back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow">Athlete profile</div>
            <h1 className="font-heading text-5xl md:text-6xl uppercase tracking-tighter mt-2" data-testid="athlete-detail-name">{p.full_name}</h1>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
              {p.sport && <span className="text-volt uppercase tracking-widest text-xs">{p.sport}</span>}
              {p.level && <span>{p.level}</span>}
              {p.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location}</span>}
              {p.club && <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> {p.club}</span>}
            </div>
          </div>
          {data.positioning_score !== null && data.positioning_score !== undefined && (
            <div className="text-right shrink-0">
              <div className="text-volt font-mono text-5xl leading-none" data-testid="athlete-detail-score">{data.positioning_score}</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1">Sponsor readiness</div>
              {data.archetype && <div className="text-xs uppercase tracking-widest mt-2">{data.archetype}</div>}
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="surface p-5 mb-8 flex flex-wrap gap-3 items-center justify-between" data-testid="athlete-action-bar">
          <div className="text-xs text-ink-muted">
            {p.min_budget_gbp ? <>Minimum partnership budget: <span className="text-white">£{p.min_budget_gbp.toLocaleString()}</span></> : "Open to all relevant partnerships"}
          </div>
          <div className="flex gap-2">
            {shortlisted ? (
              <button onClick={removeShortlist} className="btn-ghost-line text-sm inline-flex items-center gap-2" data-testid="remove-shortlist-btn">
                <BookmarkCheck className="w-4 h-4 text-volt" /> Shortlisted
              </button>
            ) : (
              <button onClick={addShortlist} className="btn-ghost-line text-sm inline-flex items-center gap-2" data-testid="add-shortlist-btn">
                <BookmarkCheck className="w-4 h-4" /> Add to shortlist
              </button>
            )}
            <button onClick={() => setShowStartModal(true)} className="btn-volt text-sm inline-flex items-center gap-2" data-testid="start-partnership-btn">
              <Send className="w-4 h-4" /> Start Partnership
            </button>
          </div>
        </div>

        {/* Contact-reveal panel */}
        <ContactRevealPanel
          status={contactStatus}
          isPro={isBrandPro(user)}
          onRequest={() => setShowContactModal(true)}
        />

        {/* Responsiveness signals */}
        {data.responsiveness && (
          <div className="mb-6" data-testid="athlete-responsiveness-block">
            <div className="label-eyebrow mb-2">Responsiveness signal</div>
            <ResponsivenessBadge r={data.responsiveness} size="lg" testid="athlete-detail-responsiveness" />
          </div>
        )}

        {/* Pull quote */}
        {data.pull_quote && (
          <div className="surface p-8 mb-6 border-l-2 border-volt" data-testid="athlete-pull-quote">
            <Quote className="w-6 h-6 text-volt mb-3" />
            <p className="font-heading text-2xl leading-snug">"{data.pull_quote}"</p>
          </div>
        )}

        {/* Two columns */}
        <div className="grid md:grid-cols-2 gap-6">
          {p.bio && (
            <Section title="About">
              <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{p.bio}</p>
            </Section>
          )}
          {p.achievements && (
            <Section title="Achievements">
              <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{p.achievements}</p>
            </Section>
          )}
          {(p.audience_size_label || data.audience_demographics) && (
            <Section title="Audience">
              {p.audience_size_label && <div className="text-sm mb-3"><span className="text-ink-muted">Total reach:</span> <span className="text-white font-medium">{p.audience_size_label}</span></div>}
              {data.audience_demographics?.engagement_rate_pct && <div className="text-sm mb-1"><span className="text-ink-muted">Engagement:</span> <span className="text-white">{data.audience_demographics.engagement_rate_pct}%</span></div>}
              {data.audience_demographics?.top_countries?.length > 0 && (
                <div className="text-sm text-ink-muted mt-2">
                  Top regions: {data.audience_demographics.top_countries.slice(0, 3).map(c => `${c.country} ${c.pct}%`).join(" · ")}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3"><Sparkles className="w-3 h-3 text-volt" /><span className="text-xs uppercase tracking-widest text-ink-muted">Verified opt-in athlete</span></div>
            </Section>
          )}
          {p.target_sponsor_categories?.length > 0 && (
            <Section title="Open to categories">
              <div className="flex flex-wrap gap-2">
                {p.target_sponsor_categories.map(c => (
                  <span key={c} className="text-xs uppercase tracking-widest border border-line px-3 py-1.5 text-ink">{c}</span>
                ))}
              </div>
            </Section>
          )}
          {data.career_highlights?.length > 0 && (
            <Section title="Career">
              <ul className="text-sm space-y-2">
                {data.career_highlights.map((c, i) => (
                  <li key={i} className="flex justify-between gap-3 border-b border-line pb-2 last:border-0">
                    <span>{c.club}{c.role && ` · ${c.role}`}</span>
                    <span className="text-ink-muted text-xs uppercase">{c.period}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {data.honours?.length > 0 && (
            <Section title="Honours">
              <ul className="text-sm space-y-1 list-disc list-inside text-ink">
                {data.honours.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </Section>
          )}
          {data.past_partnerships?.length > 0 && (
            <Section title="Past partnerships">
              <ul className="text-sm space-y-1">
                {data.past_partnerships.map((p, i) => <li key={i} className="flex justify-between"><span>{p.brand_name}</span><span className="text-ink-muted text-xs">{p.year}</span></li>)}
              </ul>
            </Section>
          )}
        </div>

        {data.share_slug && (
          <div className="text-center mt-10 text-xs text-ink-muted">
            <a href={`/kit/${data.share_slug}`} target="_blank" rel="noreferrer" className="text-volt uppercase tracking-widest hover:underline" data-testid="view-full-media-kit-link">
              View full media kit →
            </a>
          </div>
        )}

        {showStartModal && (
          <StartPartnershipModal athleteId={id} athleteName={p.full_name} onClose={() => setShowStartModal(false)} onSent={(convoId) => navigate(`/app/inbox/${convoId}`)} />
        )}
        {showContactModal && (
          <ContactRequestModal athleteId={id} athleteName={p.full_name}
            onClose={() => setShowContactModal(false)}
            onSent={() => { setShowContactModal(false); loadContact(); }} />
        )}
      </div>
    </BrandDashboardLayout>
  );
}

function ContactRevealPanel({ status, isPro, onRequest }) {
  if (!isPro && status.status === "none") {
    return (
      <div className="surface p-5 mb-8 border-l-2 border-volt" data-testid="contact-reveal-paywall">
        <div className="flex items-start gap-4">
          <Lock className="w-5 h-5 text-volt shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading text-lg uppercase">Collaborate directly with this athlete</div>
            <p className="text-xs text-ink-muted mt-1">Brand Pro unlocks AI matching, campaign workflows, sponsorship analytics — and athlete-consented direct collaboration channels.</p>
          </div>
          <Link to="/app/pricing" className="btn-ghost-line text-xs inline-flex items-center gap-2 shrink-0" data-testid="upgrade-to-brand-pro-link">
            See Brand Pro
          </Link>
        </div>
      </div>
    );
  }
  if (status.status === "none") {
    return (
      <div className="surface p-5 mb-8" data-testid="contact-reveal-cta">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-volt" />
            <div>
              <div className="font-heading text-base uppercase">Request direct collaboration</div>
              <p className="text-xs text-ink-muted mt-0.5">Open a direct channel for campaign planning. The athlete approves before any details are shared.</p>
            </div>
          </div>
          <button onClick={onRequest} className="btn-volt text-sm" data-testid="request-contact-btn">
            Request collaboration
          </button>
        </div>
      </div>
    );
  }
  if (status.status === "pending") {
    return (
      <div className="surface p-5 mb-8 border-volt/40" data-testid="contact-reveal-pending">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-volt" />
          <div>
            <div className="font-heading text-base uppercase">Collaboration request pending</div>
            <p className="text-xs text-ink-muted mt-0.5">Athlete has been notified. You'll be emailed once they respond.</p>
          </div>
        </div>
      </div>
    );
  }
  if (status.status === "denied") {
    return (
      <div className="surface p-5 mb-8" data-testid="contact-reveal-denied">
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-ink-muted" />
          <div>
            <div className="font-heading text-base uppercase text-ink-muted">Direct collaboration declined</div>
            <p className="text-xs text-ink-muted mt-0.5">No problem — the partnership inbox is just as good for moving deals forward.</p>
          </div>
        </div>
      </div>
    );
  }
  // approved — reveal contact
  const c = status.contact || {};
  return (
    <div className="surface p-5 mb-8 border-[#34C759]/40" data-testid="contact-reveal-approved">
      <div className="flex items-center gap-3 mb-4">
        <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
        <div>
          <div className="font-heading text-base uppercase text-[#34C759]">Direct collaboration unlocked</div>
          <p className="text-xs text-ink-muted mt-0.5">Athlete approved direct access. Use these responsibly — repeated abuse will be flagged across the network.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        {c.email && (
          <Row icon={Mail} label="Email" testid="contact-email">
            <a href={`mailto:${c.email}`} className="text-volt hover:underline break-all">{c.email}</a>
          </Row>
        )}
        {c.phone && (
          <Row icon={Phone} label="Phone" testid="contact-phone"><span className="text-white">{c.phone}</span></Row>
        )}
        {c.instagram_url && <Row icon={Sparkles} label="Instagram" testid="contact-ig"><a href={c.instagram_url} target="_blank" rel="noreferrer" className="text-volt hover:underline break-all">{c.instagram_url}</a></Row>}
        {c.tiktok_url && <Row icon={Sparkles} label="TikTok" testid="contact-tiktok"><a href={c.tiktok_url} target="_blank" rel="noreferrer" className="text-volt hover:underline break-all">{c.tiktok_url}</a></Row>}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-start gap-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-ink-muted mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
        {children}
      </div>
    </div>
  );
}

function ContactRequestModal({ athleteId, athleteName, onClose, onSent }) {
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (reason.trim().length < 10) return toast.error("Please add at least 10 characters of context.");
    setSending(true);
    try {
      await api.post("/brand/contact-request", { athlete_user_id: athleteId, reason });
      toast.success("Contact request sent");
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-xl" onClick={e => e.stopPropagation()} data-testid="contact-request-modal">
        <div className="p-6 border-b border-line">
          <div className="label-eyebrow">Direct collaboration</div>
          <h2 className="font-heading text-2xl uppercase tracking-tighter mt-2">Request to collaborate with {athleteName}</h2>
          <p className="text-xs text-ink-muted mt-2">Tell them what you have in mind. Athletes approve far more often when the brief is clear and specific.</p>
        </div>
        <div className="p-6 space-y-4">
          <label className="label-eyebrow block">Campaign context *</label>
          <textarea rows={5} value={reason} onChange={e => setReason(e.target.value)} maxLength={600}
            placeholder="e.g. We're launching our spring recovery line for hybrid runners and want to lock in 2 athletes for a 6-week activation. Looking to discuss creative timeline + compensation directly."
            data-testid="contact-reason-textarea"
            className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
          <div className="text-[10px] uppercase tracking-widest text-ink-muted">{reason.length}/600</div>
        </div>
        <div className="p-6 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line text-sm" data-testid="contact-cancel-btn">Cancel</button>
          <button onClick={send} disabled={sending || reason.trim().length < 10}
            className="btn-volt text-sm disabled:opacity-40" data-testid="contact-send-btn">
            {sending ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="surface p-5">
      <div className="label-eyebrow mb-3">{title}</div>
      {children}
    </div>
  );
}

function StartPartnershipModal({ athleteId, athleteName, onClose, onSent }) {
  const [form, setForm] = useState({ subject: "", body: "", budget_indication: "", campaign_window: "" });
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!form.subject || form.body.length < 10) return toast.error("Please add a subject and a clear opening message");
    setSending(true);
    try {
      const { data } = await api.post("/conversations", { athlete_user_id: athleteId, ...form });
      toast.success("Partnership conversation opened");
      onSent(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="start-partnership-modal">
        <div className="p-6 border-b border-line">
          <div className="label-eyebrow">Open partnership</div>
          <h2 className="font-heading text-3xl uppercase tracking-tighter mt-2">Start a conversation with {athleteName}</h2>
          <p className="text-xs text-ink-muted mt-2">Goes to {athleteName}'s PITCHD inbox. They (or their manager) will see it instantly. No emails are revealed at this stage.</p>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Subject *">
            <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="Q2 brand ambassador opportunity" data-testid="start-subject-input" maxLength={200}
              className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Budget indication"><input value={form.budget_indication} onChange={e => setForm(f => ({ ...f, budget_indication: e.target.value }))} placeholder="£1K-£5K" data-testid="start-budget-input" className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" /></Field>
            <Field label="Campaign window"><input value={form.campaign_window} onChange={e => setForm(f => ({ ...f, campaign_window: e.target.value }))} placeholder="Mar–Jun 2026" data-testid="start-window-input" className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" /></Field>
          </div>
          <Field label="Your opening message *">
            <textarea rows={6} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} maxLength={4000}
              placeholder="Hi! We loved your recent content on…. We're launching… and think you'd be a great fit because…"
              data-testid="start-body-textarea"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
          </Field>
        </div>
        <div className="p-6 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line text-sm" data-testid="start-cancel-btn">Cancel</button>
          <button onClick={send} disabled={sending} className="btn-volt text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="start-send-btn">
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send partnership ask"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
